import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FaCheck, FaTimes } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import { generateEventId, trackLeadSubmission } from "../../utils/analytics";

const ModalComponent = ({ show, handleClose }) => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Generate unique event ID for Meta Pixel <-> CAPI deduplication
    const eventId = generateEventId();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/PatientDetail`,
        {
          name,
          phone: phone ? `+${phone}` : "",
          email,
          message: "Fertility popup inquiry", // Required field in PatientDetail handler
          event_id: eventId,
          form_type: "fertility_popup",
          event_source_url: window.location.href, // Added for Meta CAPI tracking
        },
      );

      if (response.status === 200) {
        // Push dataLayer event for GTM -> Meta Pixel deduplication
        trackLeadSubmission("fertility_popup", eventId, { name, phone, email });

        Swal.fire({
          icon: "success",
          title: "Thank You!",
          text: "Our team will connect with you shortly to assist you.",
        });

        // Reset form fields and close modal
        setName("");
        setPhone("");
        setEmail("");
        handleClose();
      }
    } catch (error) {
      console.error("Popup form error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="custom-modal"
      centered
      size="lg"
    >
      <ModalWrapper>
        <CloseBtn onClick={handleClose}>
          <FaTimes />
        </CloseBtn>
        <LeftPane>
          <img src="/images/appointment1.jpeg" alt="Fertility" />
          <ImageOverlay />
        </LeftPane>
        <RightPane>
          <Title>Start your fertility journey with us!</Title>
          <BulletList>
            <li>
              <IconWrap>
                <FaCheck />
              </IconWrap>
              <span>25+ experienced doctors</span>
            </li>
            <li>
              <IconWrap>
                <FaCheck />
              </IconWrap>
              <span>Pan India clinic presence</span>
            </li>
            <li>
              <IconWrap>
                <FaCheck />
              </IconWrap>
              <span>Latest medical technology</span>
            </li>
          </BulletList>

          <Form onSubmit={handleFormSubmit}>
            <InputGroup>
              <Input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <PhoneInput
                country={"in"}
                enableSearch={true}
                value={phone}
                onChange={setPhone}
                inputStyle={{
                  width: "100%",
                  height: "52px",
                  borderRadius: "12px",
                  paddingLeft: "48px",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  backgroundColor: "#fdfdfd",
                }}
                buttonStyle={{
                  borderRadius: "12px 0 0 12px",
                  backgroundColor: "transparent",
                  border: "none",
                  paddingLeft: "10px",
                }}
                containerStyle={{ width: "100%" }}
              />
            </InputGroup>

            <InputGroup>
              <Input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>
            <SubmitButton type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Submit Request"}
            </SubmitButton>
          </Form>
          <PrivacyLink to="/">View our Privacy Policy</PrivacyLink>
        </RightPane>
      </ModalWrapper>
    </Modal>
  );
};

export default ModalComponent;

/* ---------------- Styled Components ---------------- */
const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 50px rgba(158, 88, 182, 0.15);
  border: 1px solid rgba(158, 88, 182, 0.1);

  @media (min-width: 768px) {
    flex-direction: row;
    min-height: 520px;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #7b7b7b;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #f8f8f8;
    color: #e85d04;
    transform: scale(1.1);
  }
`;

const LeftPane = styled.div`
  flex: 1;
  position: relative;
  display: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: 768px) {
    display: block;
    max-width: 45%;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: linear-gradient(to top, rgba(230, 126, 34, 0.6), transparent);
`;

const RightPane = styled.div`
  flex: 1;
  padding: 40px 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: 768px) {
    padding: 50px 45px;
  }
`;

const Title = styled.h3`
  font-size: 1.7rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 25px;
  line-height: 1.3;
  font-family: 'Outfit', sans-serif;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    color: #555;
    font-weight: 500;
  }
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background-color: rgba(230, 126, 34, 0.15);
  color: #e67e22;
  border-radius: 50%;
  margin-right: 12px;
  font-size: 0.7rem;
  flex-shrink: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const InputGroup = styled.div`
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.95rem;
  background-color: #fdfdfd;
  transition: all 0.3s ease;
  font-family: 'Inter', sans-serif;

  &:focus {
    outline: none;
    border-color: #e67e22;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(230, 126, 34, 0.15);
  }

  &::placeholder {
    color: #a0a0a0;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 54px;
  background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 5px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(230, 126, 34, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(230, 126, 34, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const PrivacyLink = styled(Link)`
  font-size: 0.8rem;
  color: #888;
  text-align: center;
  margin-top: 15px;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #e67e22;
    text-decoration: underline;
  }
`;
